import * as Cesium from 'cesium';

/**
 * Utility functions for 3D measurements in Cesium viewer
 * Supports distance, height, angular, and surface area measurements
 */

/**
 * Creates a point entity at the specified position
 * @param {Cesium.Cartesian3} position - World position
 * @param {Cesium.Color} color - Point color
 * @param {number} pixelSize - Point size in pixels
 * @returns {Object} Cesium entity configuration
 */
export const createMeasurePoint = (position, color = Cesium.Color.RED, pixelSize = 6) => ({
  position,
  point: { 
    pixelSize, 
    color,
    heightReference: Cesium.HeightReference.NONE,
    disableDepthTestDistance: Number.POSITIVE_INFINITY
  }
});

/**
 * Creates a line entity between two points
 * @param {Cesium.Cartesian3[]} positions - Array of world positions
 * @param {Cesium.Color} color - Line color
 * @param {number} width - Line width in pixels
 * @returns {Object} Cesium entity configuration
 */
export const createMeasureLine = (positions, color = Cesium.Color.RED, width = 2) => ({
  polyline: {
    positions,
    width,
    material: color,
    depthFailMaterial: color,
    clampToGround: false
  }
});

/**
 * Creates a label entity with measurement text
 * @param {Cesium.Cartesian3} position - Label position
 * @param {string} text - Label text
 * @param {Cesium.Color} fillColor - Text color
 * @param {Cesium.Color} backgroundColor - Background color
 * @returns {Object} Cesium entity configuration
 */
export const createMeasureLabel = (
  position, 
  text, 
  fillColor = Cesium.Color.WHITE, 
  outlineColor = Cesium.Color.BLACK
) => ({
  position,
  label: {
    text,
    font: 'bold 14px sans-serif',
    fillColor,
    showBackground: true,
    outlineColor,
    backgroundPadding: new Cesium.Cartesian2(5, 5),
    pixelOffset: new Cesium.Cartesian2(0, -20),
    disableDepthTestDistance: Number.POSITIVE_INFINITY,
    heightReference: Cesium.HeightReference.NONE
  }
});

/**
 * Calculates distance between two 3D points
 * @param {Cesium.Cartesian3} point1 - First point
 * @param {Cesium.Cartesian3} point2 - Second point
 * @returns {number} Distance in meters
 */
export const calculateDistance = (point1, point2) => {
  return Cesium.Cartesian3.distance(point1, point2);
};

/**
 * Calculates height difference between a point and ground level
 * @param {Cesium.Viewer} viewer - Cesium viewer instance
 * @param {Cesium.Cartesian3} point - 3D point
 * @returns {Object} Contains height, ground position, and distance
 */
export const calculateHeight = (viewer, point) => {
  const cartographic = Cesium.Cartographic.fromCartesian(point);
  const terrainHeight = viewer.scene.globe.getHeight(cartographic) ?? 0;
  const groundPosition = Cesium.Cartesian3.fromRadians(
    cartographic.longitude,
    cartographic.latitude,
    terrainHeight
  );
  
  const height = calculateDistance(point, groundPosition);
  
  return {
    height,
    groundPosition,
    terrainHeight,
    pointHeight: cartographic.height
  };
};

/**
 * Calculates angle between three points (vertex angle at middle point)
 * @param {Cesium.Cartesian3} point1 - First point
 * @param {Cesium.Cartesian3} vertex - Vertex point (angle measured here)
 * @param {Cesium.Cartesian3} point3 - Third point
 * @returns {Object} Angle in degrees and radians
 */
export const calculateAngle = (point1, vertex, point3) => {
  // Create vectors from vertex to each point
  const vector1 = Cesium.Cartesian3.subtract(point1, vertex, new Cesium.Cartesian3());
  const vector2 = Cesium.Cartesian3.subtract(point3, vertex, new Cesium.Cartesian3());
  
  // Normalize vectors
  Cesium.Cartesian3.normalize(vector1, vector1);
  Cesium.Cartesian3.normalize(vector2, vector2);
  
  // Calculate dot product
  const dotProduct = Cesium.Cartesian3.dot(vector1, vector2);
  
  // Calculate angle in radians, then convert to degrees
  const angleRadians = Math.acos(Cesium.Math.clamp(dotProduct, -1.0, 1.0));
  const angleDegrees = Cesium.Math.toDegrees(angleRadians);
  
  return {
    degrees: angleDegrees,
    radians: angleRadians
  };
};

/**
 * Calculates surface area of a polygon defined by points
 * Uses the shoelace formula for 2D projection, then adjusts for 3D surface
 * @param {Cesium.Cartesian3[]} points - Array of points defining the polygon
 * @returns {number} Surface area in square meters
 */
export const calculateSurfaceArea = (points) => {
  if (points.length < 3) return 0;
  
  // For 3D surface area calculation, we'll use triangulation
  // Convert to triangles and sum their areas
  let totalArea = 0;
  
  // Use the first point as a common vertex for triangulation
  const firstPoint = points[0];
  
  for (let i = 1; i < points.length - 1; i++) {
    const point2 = points[i];
    const point3 = points[i + 1];
    
    // Calculate area of triangle using cross product
    const vector1 = Cesium.Cartesian3.subtract(point2, firstPoint, new Cesium.Cartesian3());
    const vector2 = Cesium.Cartesian3.subtract(point3, firstPoint, new Cesium.Cartesian3());
    
    const crossProduct = Cesium.Cartesian3.cross(vector1, vector2, new Cesium.Cartesian3());
    const triangleArea = 0.5 * Cesium.Cartesian3.magnitude(crossProduct);
    
    totalArea += triangleArea;
  }
  
  return totalArea;
};

/**
 * Formats measurement values for display
 * @param {number} value - Measurement value
 * @param {string} unit - Unit of measurement
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted string
 */
export const formatMeasurement = (value, unit, decimals = 2) => {
  return `${value.toFixed(decimals)} ${unit}`;
};

/**
 * Gets the center point of an array of positions
 * @param {Cesium.Cartesian3[]} positions - Array of positions
 * @returns {Cesium.Cartesian3} Center position
 */
export const getCenterPoint = (positions) => {
  if (positions.length === 0) return new Cesium.Cartesian3();
  if (positions.length === 1) return positions[0];
  if (positions.length === 2) {
    return Cesium.Cartesian3.midpoint(positions[0], positions[1], new Cesium.Cartesian3());
  }
  
  // For multiple points, calculate centroid
  const sum = positions.reduce(
    (acc, pos) => Cesium.Cartesian3.add(acc, pos, acc),
    new Cesium.Cartesian3()
  );
  
  return Cesium.Cartesian3.multiplyByScalar(
    sum, 
    1.0 / positions.length, 
    new Cesium.Cartesian3()
  );
};